import { Inject, Injectable, DOCUMENT } from "@angular/core";
import { drctJsFunctions, DRCTMessage, DRCTPaymentReceipt, DRCTPayments, DRCTWebAll } from '@drct-web-apps/ui-types';
import { UiServicesLogger } from "./ui-services.logger.service";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { UiServicesUtils } from "./ui-services.utils.service";
import { UiServicesMessage } from "./ui-services.message.service";
import { UiServicesRegsitration } from "./ui-services.registration.service";



@Injectable({
  providedIn: 'root'
})
export class UiServicesPayments {
    private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    private readonly _paymentEvents = new Subject<string>();

    get paymentEvents(): Observable<string> {
        return this._paymentEvents.asObservable();
    }

    private readonly _paymentLibReady: ReplaySubject<boolean> = new ReplaySubject(1);
    get paymentLibReady(): Observable<boolean> {
        return this._paymentLibReady.asObservable();
    }
    set paymentLibReady(value: boolean) {
        if(value) {
            this._paymentLibReady.next(true);
        }
    }

    private referenceNumber!: string;
    private paymentsInitialized = false;
    
    constructor(private readonly logger: UiServicesLogger, private readonly http: HttpClient, 
                private readonly utils: UiServicesUtils, private readonly msgBus: UiServicesMessage,
                private readonly register: UiServicesRegsitration, @Inject(DOCUMENT) private readonly document: Document) { 
    }

    public startMessageListening(): void {
        this.logger.log("Payments Service: Start Message Listening");
        this.msgBus.onMessage("PAYMENTS")
                   .pipe(takeUntil(this.destroyed$))
                   .subscribe({
                        next: (message: DRCTMessage) => {
                            this.onDRCTPaymentEvents(message);
                        }
                    });
    }

    public OnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
        this.paymentsInitialized = false;
        this.referenceNumber = "";
    }

    public initPayments(env: string): void {
        if(!this.paymentsInitialized) {
            drctJsFunctions.initCheckout(env);
            this.paymentsInitialized = true;
            this.logger.log("Payments Service: Initialized");
        }
    }

    public getRefeNumberAndStartCheckout(payLoadData: DRCTPayments): void {
        const registerForm = new FormData();
        registerForm.append('data', JSON.stringify(payLoadData));

        this.logger.log("Payments Service: Get Reference Number");
        this.referenceNumber = "";
        
        this.http.post(this.utils.builApiUrlWithEnv("drct_payment_ref_no.php"), registerForm)
                 .pipe(takeUntil(this.destroyed$))
                 .subscribe({
                    next:(response: DRCTWebAll) => {
                        this.logger.log("Payments Service: Reference Number Response Received. Status: " + response.success);
                        if(response.success) {
                            this.referenceNumber = response.ticket_no ?? "";
                            this.openPaymentWidget();
                            drctJsFunctions.startCheckout(this.referenceNumber);
                        } 
                        else {
                            this._paymentEvents.next("PAYMENT_GATEWAY");   
                        }
                    },
                    error: (error) => {
                        this.logger.error("Payments Service: Reference Number Error");
                        this._paymentEvents.next("PAYMENT_GATEWAY");
                    }
                 });
    }

    public getPaymentReceipt(payLoadData: DRCTPayments | null): void {
        if(payLoadData) {
            payLoadData.ticket_no = this.referenceNumber;
        }
        
        const receiptForm = new FormData();
        receiptForm.append('data', JSON.stringify(payLoadData));

        this.logger.log("Payments Service: Get Receipt Data");
        this.utils.showHidePageLoader(true);
        
        this.http.post(this.utils.builApiUrlWithEnv("drct_payment_receipt.php"), receiptForm)
                 .pipe(takeUntil(this.destroyed$))
                 .subscribe({
                    next:(response: DRCTPaymentReceipt) => {
                        this.logger.log("Payments Service: Receipt Data Received. Status: " + response.success);
                        if(response.success) {
                            this.register.receiptData = response;
                            if (this.register.paymentData && response.attendees)
                                this.register.paymentData.attendees = response.attendees;
                    
                            this._paymentEvents.next("PAYMENT_SUCCESS");
                        } 
                        else {
                            this._paymentEvents.next("PAYMENT_ERROR");
                        }
                    },
                    error: (error) => {
                        this.logger.error("Payments Service: Receipt Data Error");
                        this._paymentEvents.next("PAYMENT_ERROR");    
                    },
                    complete: () => {
                        this.closePaymentWidget();
                        drctJsFunctions.closeCheckout();
                    }
                 });
    }

    public onDRCTPaymentEvents(msg: DRCTMessage): void {
        this.logger.log("Payments Service: Payment Event Received. Type: " + msg.subType);
        switch(msg.subType) {
            case "INIT_CHECKOUT":
                this.logger.log("INIT_CHECKOUT");
                break;
            case "PAGE_LOADED":
                this.logger.log("onMonerisPageLoad: " + msg.message);
                break;
            case "TRANSACTION_CANCEL":
            case "TRANSACTION_ERROR":
                this.logger.log("onTransactionCancel/Error: " + msg.message);
                this.closePaymentWidget();
                drctJsFunctions.closeCheckout();
                this._paymentEvents.next("PAYMENT_ERROR");
                break;
            case "PAYMENT_SUBMIT":
                this.logger.log("onPaymentSubmit: " + msg.message);
                this.hidePaymentWidgetCloseButton();
                break;
            case "PAYMENT_RECEIPT":
                this.logger.log("onPaymentReceipt: " + msg.message);
                this._paymentEvents.next("RECEIPT_READY");
                break;
            case "PAYMENT_COMPLETE":
                this.logger.log("onPaymentComplete: " + msg.message);
                break;
        }
    }

    public closePaymentWidget(): void {
        this.document.getElementById("divDRCTPaymentWidget")?.classList.remove("showPayment");
        this.showPaymentWidgetCloseButton();
    }

    public showPaymentWidgetCloseButton(): void {
        this.document.getElementById("btnPaymentClose")?.classList.remove("btn-close--hide");
    }

    public hidePaymentWidgetCloseButton(): void {
        this.document.getElementById("btnPaymentClose")?.classList.add("btn-close--hide");
    }

    public openPaymentWidget(): void {
        this.showPaymentWidgetCloseButton();
        this.document.getElementById("divDRCTPaymentWidget")?.classList.add("showPayment");
    }

    public onPaymentWindowCloseClick(): void {
        this.closePaymentWidget();
        drctJsFunctions.closeCheckout();
        this.logger.log("Payments Service: Payment Window Closed By User..");
        this._paymentEvents.next("CHECK_OUT_CLOSED");
    }
}